'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';

import type { TDynamicData } from '@eliadmin/core/src/api/dynamics/dynamics-api';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import Image from '@eliadmin/core/src/components/image/image-wrapped';

const STEP_DURATION_MS = 2000;

export function PartnerProcess({
  subtitle,
  title,
  image,
  steps,
}: {
  subtitle: string;
  title: string;
  image: string;
  steps: TDynamicData[];
}) {
  const sectionRef = useRef<HTMLElement>(null);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [completedIndices, setCompletedIndices] = useState<Set<number>>(new Set());
  const [progress, setProgress] = useState<number>(0);
  const [hasStarted, setHasStarted] = useState(false);

  const startAnimation = useCallback(() => {
    if (hasStarted || steps.length === 0) return;
    setHasStarted(true);
    setActiveIndex(0);
  }, [hasStarted, steps.length]);

  // Intersection Observer — triggers once on enter
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          startAnimation();
          observer.disconnect();
        }
      },
      { threshold: 0.3 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [startAnimation]);

  // Progress bar + step advancement
  useEffect(() => {
    if (activeIndex < 0 || activeIndex >= steps.length) return;

    setProgress(0);

    const startTime = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const next = Math.min((elapsed / STEP_DURATION_MS) * 100, 100);
      setProgress(next);

      if (next < 100) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setCompletedIndices(prev => new Set(prev).add(activeIndex));

        const nextIndex = activeIndex + 1;
        if (nextIndex < steps.length) {
          setActiveIndex(nextIndex);
        } else {
          // All done — mark last as completed, clear active
          setActiveIndex(-1);
        }
      }
    };

    const rafRef = { current: 0 };
    rafRef.current = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(rafRef.current);
  }, [activeIndex, steps.length]);

  return (
    <section
      id="proces"
      ref={sectionRef}
      className={cn('custom-section', 'bg-[#F2EDE6] py-8 sm:py-10 lg:py-[50px]')}
    >
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-16 md:flex-row md:items-center md:justify-between',
        )}
      >
        <div className="flex flex-col items-start justify-start gap-6 sm:gap-8 md:max-w-[539px] lg:gap-10">
          <div className="flex w-full flex-col items-start justify-start gap-4 sm:gap-5 lg:gap-6">
            <h4 className="text-sm font-medium uppercase text-primary sm:text-base">{subtitle}</h4>
            <Heading title={title} />
          </div>
          <div className="flex w-full flex-col items-start justify-start gap-4 md:max-w-[539px]">
            {steps.map((step, index) => {
              const isCompleted = completedIndices.has(index);
              const isActive = activeIndex === index;

              return (
                <React.Fragment key={index}>
                  <StepRow step={step} index={index} isActive={isActive || isCompleted} />
                  <ProgressDivider
                    isCompleted={isCompleted}
                    isActive={isActive}
                    progress={isActive ? progress : isCompleted ? 100 : 0}
                  />
                </React.Fragment>
              );
            })}
          </div>
        </div>
        <Image
          src={image}
          alt={subtitle}
          width={0}
          height={0}
          className="h-auto w-full max-w-[652px] object-cover object-center"
          sizes="100vw"
        />
      </div>
    </section>
  );
}

function ProgressDivider({
  isCompleted,
  isActive,
  progress,
}: {
  isCompleted: boolean;
  isActive: boolean;
  progress: number;
}) {
  return (
    <div className="relative flex h-[2px] w-full items-center justify-start bg-[#3A332C33]">
      <div
        className={cn('h-[2px] bg-primary transition-none')}
        style={{
          width: `${progress}%`,
          transition: isActive ? 'none' : isCompleted ? 'none' : undefined,
        }}
      />
    </div>
  );
}

function StepRow({
  step,
  index,
  isActive,
}: {
  step: TDynamicData;
  index: number;
  isActive?: boolean;
}) {
  return (
    <div className={cn('flex w-full items-center justify-start gap-4 lg:gap-6')}>
      <div
        className={cn(
          'flex h-10 min-w-10 max-w-10 items-center justify-center rounded-full text-center text-lg font-semibold uppercase tracking-tighter text-white transition-colors duration-500 sm:text-xl lg:h-14 lg:min-w-14 lg:max-w-14 lg:text-2xl',
          isActive ? 'bg-primary' : 'bg-[#D3C7B8]',
        )}
      >
        0{index + 1}
      </div>
      <p
        className={cn(
          'text-lg font-medium tracking-tight text-[#636264] transition-colors duration-500 sm:text-xl lg:text-2xl',
          isActive && 'text-primary',
        )}
      >
        {step.name}
      </p>
    </div>
  );
}
